import { Audience } from "@/types";

import * as T from "./actionTypes";
import {
  addQueryGroup,
  addUploadFile,
  addVariables,
  changeBuildMode,
  changeName,
  changeOperator,
  changeTravelTime,
  changeUploadMode,
  changeVariables,
  createDefinition,
  removeQueryGroup,
  removeVariable,
  resetQueryGroup,
  setGeoFence,
  setSites,
  setSiteCatchment,
  setSiteCategories,
  setSiteDayType
} from "./helpers";
import { AudienceAction, AudienceState } from "./types";

type AudienceOfType<K extends Audience["audience_type"]> = Extract<Audience, { audience_type: K }>;

/**
 * Applies a change to the current audience if it's of the given type.
 * Returns the same state when there's no change, so consumers don't re-render.
 */
const updateAudience = <K extends Audience["audience_type"]>(
  state: AudienceState,
  type: K,
  update: (audience: AudienceOfType<K>) => Audience
): AudienceState => {
  const audience = state.currentAudience;
  if (!audience || audience.audience_type !== type) return state;

  const updated = update(audience as AudienceOfType<K>);
  return updated === audience ? state : { ...state, currentAudience: updated };
};

export const audienceReducer = (
  state: AudienceState,
  action: AudienceAction
): AudienceState => {

  switch (action.type) {

    // ---- Current audience ----

    case T.RESET:
      return { ...state, currentAudience: null, currentSegment: null, currentDefinition: null };

    case T.CREATE_AUDIENCE:
      return { ...state, currentAudience: null, currentSegment: null };

    case T.LOAD_AUDIENCE:
      return {
        ...state,
        currentAudience: action.payload,
        currentDefinition: createDefinition(action.payload),
        currentSegment: null,
      };

    // The audience has been saved, so it's no longer changed
    case T.SAVED_AUDIENCE:
      return {
        ...state,
        currentAudience: action.payload,
        currentDefinition: createDefinition(action.payload),
      };

    case T.BUILD_AUDIENCE:
      if (!state.currentAudience) return state;
      return {
        ...state,
        currentAudience: {
          ...state.currentAudience,
          audience_id: action.payload.audience_id ?? "",
          audience_geo: action.payload.audience_geo,
          audience_size: action.payload.audience_size,
        },
        currentSegment: null,
      };

    case T.ADD_SEGMENTS:
      if (!state.currentAudience) return state;
      return {
        ...state,
        currentAudience: { ...state.currentAudience, segment: true, segments: action.payload },
      };

    case T.CHANGE_SEGMENT:
      if (state.currentSegment === action.payload) return state;
      return { ...state, currentSegment: action.payload };

    // Only the audience being viewed changes; cached lists are updated by the mutation
    case T.CHANGE_NAME: {
      const { audienceId, audienceName, projectName } = action.payload;
      if (state.currentAudience?.audience_id !== audienceId) return state;
      return {
        ...state,
        currentAudience: changeName(state.currentAudience, audienceName, projectName),
      };
    }

    case T.SHARE_AUDIENCE: {
      const { audienceId, userIds } = action.payload;
      if (state.currentAudience?.audience_id !== audienceId) return state;
      return {
        ...state,
        currentAudience: { ...state.currentAudience, shared_with: userIds },
      };
    }

    // ---- Build audiences ----

    case T.ADD_VARIABLE_GROUP:
      return updateAudience(state, "build", addQueryGroup);

    case T.REMOVE_VARIABLE_GROUP:
      return updateAudience(state, "build", (a) => removeQueryGroup(a, action.payload));

    case T.RESET_VARIABLE_GROUP:
      return updateAudience(state, "build", (a) => resetQueryGroup(a, action.payload));

    case T.CHANGE_OPERATOR:
      return updateAudience(state, "build", (a) => changeOperator(a, action.payload));

    case T.CHANGE_GROUP_OPERATOR:
      return updateAudience(state, "build", (a) =>
        changeOperator(a, action.payload.operator, action.payload.id)
      );

    case T.CHANGE_GROUP_VARIABLES:
      return updateAudience(state, "build", (a) =>
        changeVariables(a, action.payload.id, action.payload.variables)
      );

    case T.ADD_VARIABLES:
      return updateAudience(state, "build", (a) =>
        addVariables(a, action.payload.id, action.payload.variables)
      );

    case T.REMOVE_VARIABLE:
      return updateAudience(state, "build", (a) =>
        removeVariable(a, action.payload.id, action.payload.tagId)
      );

    case T.CHANGE_BUILD_MODE:
      return updateAudience(state, "build", (a) => changeBuildMode(a, action.payload));

    case T.SET_GEO_FENCE:
      return updateAudience(state, "build", (a) => setGeoFence(a, action.payload));

    // ---- Upload audiences ----

    case T.ADD_UPLOAD_FILE:
      return updateAudience(state, "upload", (a) => addUploadFile(a, action.payload));

    case T.CLEAR_UPLOAD_FILE:
      return updateAudience(state, "upload", (a) => addUploadFile(a, null));

    case T.CHANGE_UPLOAD_MODE:
      return updateAudience(state, "upload", (a) => changeUploadMode(a, action.payload));

    case T.CHANGE_TRAVELTIME:
      return updateAudience(state, "upload", (a) => changeTravelTime(a, action.payload));

    case T.CHECK_UPLOAD_FILE:
      if (state.validFile === action.payload) return state;
      return { ...state, validFile: action.payload };

    // ---- Site audiences ----

    case T.SET_SITES:
      return updateAudience(state, "site", (a) => setSites(a, action.payload));

    case T.SET_SITE_CATCHMENT:
      return updateAudience(state, "site", (a) => setSiteCatchment(a, action.payload));

    case T.SET_SITE_CATEGORIES:
      return updateAudience(state, "site", (a) => setSiteCategories(a, action.payload));

    case T.SET_SITE_DAY_TYPE:
      return updateAudience(state, "site", (a) => setSiteDayType(a, action.payload));

    default:
      return state;
  }
};
